import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const DemoLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking"); // "checking" | "logging_in" | "unavailable" | "error"
  
  // Check for reset parameter
  const shouldReset = searchParams.get("reset") === "1" || searchParams.get("reset") === "true";

  useEffect(() => {
    const performDemoLogin = async () => {
      try {
        // First check if demo login is available
        const statusRes = await axios.get(`${API}/auth/demo-status`);
        
        if (!statusRes.data.demo_available) {
          setStatus("unavailable");
          return;
        }

        setStatus("logging_in");

        // Perform demo login with optional reset
        const loginRes = await axios.post(
          `${API}/auth/demo-login?reset=${shouldReset}`,
          {},
          { withCredentials: true }
        );

        let authenticatedUser = loginRes.data.user;
        for (let attempt = 0; attempt < 5; attempt += 1) {
          try {
            const authRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
            authenticatedUser = authRes.data;
            break;
          } catch (authError) {
            if (attempt === 4) throw authError;
            await wait(250 * (attempt + 1));
          }
        }

        if (shouldReset && loginRes.data.reset_performed) {
          toast.success("Demo reset complete - starting fresh");
        } else {
          toast.success("Demo login successful");
        }
        navigate(loginRes.data.redirect_to, {
          replace: true,
          state: { user: authenticatedUser },
        });
      } catch (error) {
        console.error("Demo login error:", error);
        if (error.response?.status === 403) {
          setStatus("unavailable");
        } else {
          setStatus("error");
          toast.error("Demo login failed");
        }
      }
    };

    performDemoLogin();
  }, [navigate, shouldReset]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header sticky={false} />

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="text-center max-w-md">
          {(status === "checking" || status === "logging_in") && (
            <>
              <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
              <h1 className="font-manrope font-semibold text-xl text-foreground mb-2">
                {status === "checking" 
                  ? "Checking demo status..." 
                  : shouldReset 
                    ? "Refreshing demo session..." 
                    : "Signing you in..."}
              </h1>
              <p className="text-muted-foreground">
                {shouldReset ? "Refreshing the guided demo with seeded plan access" : "Setting up your demo session"}
              </p>
            </>
          )}

          {status === "unavailable" && (
            <>
              <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-4" />
              <h1 className="font-manrope font-semibold text-xl text-foreground mb-2">
                Demo Login Unavailable
              </h1>
              <p className="text-muted-foreground mb-6">
                Demo login is disabled in the production environment.
              </p>
              <Link 
                to="/auth" 
                data-testid="demo-login-sign-in-link"
                className="inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground rounded-full font-medium hover:bg-primary/90 transition-colors"
              >
                Sign in with email
              </Link>
            </>
          )}

          {status === "error" && (
            <>
              <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-4" />
              <h1 className="font-manrope font-semibold text-xl text-foreground mb-2">
                Something went wrong
              </h1>
              <p className="text-muted-foreground mb-6">
                Unable to complete demo login. Please try again.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => window.location.reload()}
                  data-testid="demo-login-retry-button"
                  className="inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground rounded-full font-medium hover:bg-primary/90 transition-colors"
                >
                  Try again
                </button>
                <Link 
                  to="/auth" 
                  data-testid="demo-login-normal-sign-in-link"
                  className="inline-flex items-center justify-center px-6 py-3 border border-border rounded-full font-medium hover:bg-muted transition-colors"
                >
                  Sign in normally
                </Link>
              </div>
            </>
          )}

          {/* Demo mode indicator */}
          {(status === "checking" || status === "logging_in") && (
            <div className="mt-8 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-700">
                <strong>Demo Mode:</strong> This bypass is for walkthroughs only and is disabled in production.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DemoLogin;
